import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, MapPin, Upload, Loader2, X, Briefcase } from "lucide-react";
import { JOB_LISTINGS } from "../data";
import { api } from "../lib/api";
import { SmsConsentCheckbox } from "../components/SmsConsentCheckbox";

const ease = [0.16, 1, 0.3, 1];
const ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_BYTES = 5 * 1024 * 1024;

export function Careers() {
  const [selectedJob, setSelectedJob] = useState(null);

  return (
    <div data-testid="careers-page" className="page-enter pt-32 sm:pt-40">
      <section className="container-x pb-14">
        <p className="text-xs uppercase tracking-[0.25em] text-[#E9610E] font-semibold mb-4">Careers</p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease }}
          className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-balance max-w-5xl text-[#0F1E3C]"
        >
          Your next role,
          <br />
          <span className="italic font-serif gradient-text-amber">matched with care.</span>
        </motion.h1>
        <p className="mt-7 text-lg text-[#475569] max-w-2xl">
          Every role we post is a live search with a real hiring manager. No ghosting, no
          pipeline stuffing, no recycled JDs. Apply once — we&rsquo;ll place you right.
        </p>
      </section>

      <section className="container-x pb-24">
        <div className="card-soft rounded-3xl overflow-hidden bg-white">
          {JOB_LISTINGS.map((job, i) => (
            <motion.button
              key={job.id}
              onClick={() => setSelectedJob(job)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.05, ease }}
              className="w-full text-left p-6 sm:p-8 border-b border-[#EAE3D2] last:border-b-0 hover:bg-[#FAF7F2] transition-colors group flex flex-col md:flex-row md:items-center gap-4"
              data-testid={`job-listing-${job.id}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#FFF4EA] text-[#E9610E] flex items-center justify-center shrink-0">
                <Briefcase className="w-5 h-5" />
              </div>
              <div className="md:flex-1">
                <div className="flex flex-wrap items-center gap-2 text-xs text-[#64748B] mb-1.5">
                  <span className="font-semibold text-[#E9610E]">{job.industry}</span>
                  <span>·</span>
                  <span>{job.posted}</span>
                </div>
                <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight text-[#0F1E3C] mb-1.5">
                  {job.title}
                </h3>
                <div className="flex flex-wrap items-center gap-3 text-sm text-[#475569]">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {job.location}
                  </span>
                  <span>·</span>
                  <span>{job.type}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold text-[#E9610E] group-hover:gap-3 transition-all">
                Apply now <ArrowUpRight className="w-4 h-4" />
              </div>
            </motion.button>
          ))}
        </div>

        <div className="mt-8 card-soft rounded-2xl p-8 text-center bg-white">
          <p className="text-sm text-[#475569] mb-4">Don&rsquo;t see your role? We place hundreds we don&rsquo;t list.</p>
          <button
            data-testid="general-apply-btn"
            onClick={() =>
              setSelectedJob({ id: "general", title: "General Application", location: "Any", type: "Open", industry: "Any" })
            }
            className="btn-outline"
          >
            Submit a general application
          </button>
        </div>
      </section>

      <ApplicationDialog job={selectedJob} onClose={() => setSelectedJob(null)} />
    </div>
  );
}

function ApplicationDialog({ job, onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", experience_years: "",
    skills: "", linkedin_url: "", message: "",
  });
  const [resume, setResume] = useState(null);
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ACCEPTED_TYPES.includes(f.type)) {
      toast.error("Please upload a PDF, DOC, or DOCX file.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Resume must be 5MB or smaller.");
      e.target.value = "";
      return;
    }
    setResume(f);
  }

  async function submit(e) {
    e.preventDefault();
    if (!job) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      if (form.phone) fd.append("phone", form.phone);
      fd.append("position", job.title);
      if (form.experience_years) fd.append("experience_years", form.experience_years);
      if (form.skills) fd.append("skills", form.skills);
      if (form.linkedin_url) fd.append("linkedin_url", form.linkedin_url);
      if (form.message) fd.append("message", form.message);
      if (resume) fd.append("resume", resume);
      fd.append("sms_consent", String(smsConsent));

      await api.post("/careers/apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Application submitted. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", experience_years: "", skills: "", linkedin_url: "", message: "" });
      setResume(null);
      setSmsConsent(false);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AnimatePresence>
      {job && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
          data-testid="application-dialog"
        >
          <div className="absolute inset-0 bg-[#0F1E3C]/40 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 16 }}
            transition={{ duration: 0.3, ease }}
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white border border-[#EAE3D2] p-8 sm:p-10 shadow-2xl"
          >
            <button
              onClick={onClose}
              data-testid="dialog-close"
              className="absolute top-5 right-5 w-10 h-10 rounded-full bg-[#FAF7F2] border border-[#EAE3D2] flex items-center justify-center hover:bg-[#FFF4EA]"
            >
              <X className="w-4 h-4" />
            </button>

            <p className="text-xs uppercase tracking-[0.2em] text-[#E9610E] font-semibold mb-2">Apply</p>
            <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-[#0F1E3C] mb-2">{job.title}</h2>
            {job.location !== "Any" && (
              <p className="text-sm text-[#475569] mb-7">
                {job.location} · {job.type}
              </p>
            )}

            <form onSubmit={submit} className="space-y-4">
              <Field label="Full name" required>
                <input data-testid="app-name-input" type="text" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Email" required>
                  <input data-testid="app-email-input" type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" />
                </Field>
                <Field label="Phone">
                  <input data-testid="app-phone-input" type="tel" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Years of experience">
                  <input data-testid="app-exp-input" type="number" min="0" value={form.experience_years}
                    onChange={(e) => setForm({ ...form, experience_years: e.target.value })} className="input" />
                </Field>
                <Field label="LinkedIn URL">
                  <input data-testid="app-linkedin-input" type="url" value={form.linkedin_url}
                    onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} className="input"
                    placeholder="https://linkedin.com/in/..." />
                </Field>
              </div>
              <Field label="Key skills">
                <input data-testid="app-skills-input" type="text" value={form.skills}
                  onChange={(e) => setForm({ ...form, skills: e.target.value })} className="input"
                  placeholder="e.g. PLC, Six Sigma, AWS" />
              </Field>
              <Field label="Cover note">
                <textarea data-testid="app-message-input" rows="4" value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })} className="input resize-none"
                  placeholder="A few lines about why this role." />
              </Field>
              <Field label="Resume (PDF, DOC, DOCX · max 5MB)">
                <label className="flex items-center gap-3 px-4 py-3 border border-[#EAE3D2] rounded-xl cursor-pointer hover:bg-[#FAF7F2] transition-colors">
                  <Upload className="w-4 h-4 text-[#E9610E]" />
                  <span className="text-sm text-[#475569] flex-1 truncate">
                    {resume ? resume.name : "Choose a file"}
                  </span>
                  <input data-testid="app-resume-input" type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFile} className="hidden" />
                </label>
              </Field>

              <div className="pt-2">
                <SmsConsentCheckbox checked={smsConsent} onChange={setSmsConsent} testId="app-sms-consent" />
              </div>

              <button type="submit" data-testid="app-submit-btn" disabled={loading} className="btn-accent w-full">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit application <ArrowUpRight className="w-4 h-4" /></>}
              </button>
            </form>

            <style>{`
              .input {
                width: 100%;
                background: #FFFFFF;
                border: 1px solid #EAE3D2;
                border-radius: 12px;
                padding: 12px 16px;
                color: #0F1E3C;
                font-size: 14px;
                transition: border-color 0.2s, box-shadow 0.2s;
              }
              .input:focus {
                outline: none;
                border-color: #E9610E;
                box-shadow: 0 0 0 3px rgba(233,97,14,0.1);
              }
            `}</style>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.1em] text-[#64748B] font-semibold block mb-2">
        {label}
        {required && <span className="text-[#E9610E] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
